/**
 * La taille du décompte, tirée de la largeur de SA zone, pas de celle de la fenêtre. Pur.
 *
 * Dans le live au grand écran, le décompte se joue dans le volet de l'activité (420 px), bien plus étroit
 * que la fenêtre : une taille tirée de la fenêtre y faisait repasser « C'est parti ! » sur deux lignes.
 *
 * Deux règles, chacune sur la largeur de la zone :
 *  - « C'est parti ! » prend la plus grande taille qui tient sur une ligne, entre le titre (`text-4xl`, le
 *    plancher lisible) et trois fois le titre. Sous le plancher, une ligne ne tient plus : le départ reste au
 *    plancher et passe sur deux lignes ;
 *  - les chiffres ne suivent plus le texte (Wajih, 20/10 : « validé + grand ») : le dessin prend les trois
 *    quarts de la zone, jusqu'à sa taille pleine (`START_COUNTDOWN_SIZE`). Le glyphe d'un chiffre fait 36 %
 *    du dessin : à 390, 97 px de haut, contre 65 quand il suivait le texte (`countdownFit.test.ts`).
 */
import { fontSizes } from "../../theme/tokens/typography"
import { START_COUNTDOWN_SIZE } from "./startCountdownAnimation"

/**
 * La largeur de « C'est parti ! » en Outfit 800 pour 1 px de taille de police (320 px à 60 px), arrondie
 * au-dessus. `countdownFit.test.ts` la remesure dans la police.
 */
export const GO_WIDTH_PER_PX = 5.34

/** Le départ au plus grand : trois fois le titre. */
export const GO_MAX = fontSizes["text-4xl"] * 3
/** Le plancher lisible : le titre. */
export const GO_FLOOR = fontSizes["text-4xl"]

export type CountdownFit = {
    /** La taille de police de « C'est parti ! ». */
    goSize: number
    /** Le côté du dessin des chiffres (son JSON est fait pour `START_COUNTDOWN_SIZE`, au plus grand). */
    drawingSize: number
    /** La taille de police d'un chiffre en texte (mouvement réduit) : la moitié du dessin, comme dans le dessin. */
    digitSize: number
    /** « C'est parti ! » tient-il sur une ligne ? */
    oneLine: boolean
}

/** La part de la zone que prend le dessin des chiffres. */
export const DRAWING_ZONE_SHARE = 0.75

/** La taille du décompte dans une zone de `width` px. */
export function countdownFit(width: number): CountdownFit {
    const fitting = Math.floor(width / GO_WIDTH_PER_PX)
    const goSize = Math.max(GO_FLOOR, Math.min(GO_MAX, fitting))
    const drawingSize = Math.min(START_COUNTDOWN_SIZE, Math.round(width * DRAWING_ZONE_SHARE))
    return { goSize, drawingSize, digitSize: Math.round(drawingSize / 2), oneLine: fitting >= GO_FLOOR }
}
